<template>
  <modal
    v-model="show"
    :footer="!saving"
    @hide="closeModal"
    title="Save Resource to Library">
    <ChooseStudentForm
      v-if="!saving && show"
      :preselected-student="selectedStudent"
      :recent-students="recentStudents"
      @selectedStudent:existing="choseStudent"
      @selectedStudent:new="choseStudent"
      @tabChange="handleTabChange"
      :content-type="contentType"
    />

    <template v-if="saving">
      <p class="text-muted"><i class="fa fa-circle-o-notch fa-spin"/> Saving...</p>
    </template>

    <alert
      type="success"
      :dismissible="true"
      @dismissed="showSaveSuccess = !showSaveSuccess"
      v-if="showSaveSuccess">
      <p>Resource was saved to <a :href="toolkitUrls.libraryFolder(saveResult.uuid)">{{ saveResult.name }}</a></p>
    </alert>

    <alert
      type="danger"
      :dismissible="true"
      @dismissed="showError = !showError"
      v-if="showError">
      <p>{{ saveError }}</p>
    </alert>

    <div
      v-if="!saving"
      class="btn-toolbar"
      slot="footer">
      <button
        v-if="!saveResult"
        class="btn btn-link btn-lg"
        @click="closeModal()">Cancel</button>
      <button
        v-if="saveResult"
        class="btn btn-default btn-lg"
        @click="closeModal()">Done</button>
      <button
        v-if="hasSelectedAStudent"
        class="btn btn-success btn-lg"
        :disabled="!selectedStudent"
        @click="save">
        <template v-if="!saving">
          <span
            v-if="isSharedWithMeTabActive"
            class="material-icons send-icon"
            aria-hidden="true">send
          </span>
          {{ saveFolderButtonPrefix }}
          {{ selectedStudent && truncatedStudentName || 'a folder' }}
        </template>
      </button>
    </div>
  </modal>
</template>

<script>
import { libraryQuotasMixin } from '@/components/library/library_quotas_mixin'
import ChooseStudentForm from '@/components/library/ChooseStudentForm'
import { addStudent, postStudentContent } from '@/api/library'
import { mapState, mapGetters } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { toolkitUrls } from '@/links'

export default {
  name: 'SaveResourceModal',
  mixins: [libraryQuotasMixin],
  components: {
    ChooseStudentForm
  },
  props: {
    contentUuid: {
      type: String,
      required: true
    },
    open: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    toolkitUrls() {
      return toolkitUrls
    },
    ...mapState('library', ['students', 'recentStudents']),
    ...mapState('user', ['user']),
    ...mapGetters('library', ['getUserRoleForFolder']),
    saveFolderButtonPrefix() {
      return this.isSharedWithMeTabActive ? 'Contribute to' : 'Save to';
    },
    truncatedStudentName() {
      const name = this.selectedStudent.name
      return name.length > 20 ? name.substring(0, 20) + '...' : name
    },
    contentType() {
     return CONTENT_TYPES.RESOURCE
    },
    hasSelectedAStudent() {
      return (this.selectedStudent && this.saveResult && this.selectedStudent.uuid !== this.saveResult.uuid) || !this.saveResult
    }
  },
  data() {
    return {
      show: this.open,
      selectedStudent: null,
      saving: false,
      showSaveSuccess: false,
      showError: false,
      saveError: '',
      saveResult: null,
      isSharedWithMeTabActive: false
    }
  },
  methods: {
    closeModal() {
      this.$emit('update:open', false)
    },
    choseStudent(student) {
      this.selectedStudent = student
    },
    async save() {
      if (!this.selectedStudent) {
        return
      }
      if (
        this.selectedStudent.uuid &&
        this.maxContentReachedForStudent(CONTENT_TYPES.RESOURCE, this.selectedStudent.uuid)
      ) {
        this.showError = true
        this.saveError = this.maxContentTypeErrorMessage(CONTENT_TYPES.RESOURCE)
        this.sendMaxContentInFolderEvent(CONTENT_TYPES.RESOURCE, this.selectedStudent.uuid)
        return
      }

      let isNewStudent = !this.selectedStudent.uuid
      this.saving = true
      this.showError = false
      this.showSaveSuccess = false

      try {
        if (isNewStudent) {
          let newStudentResp = await addStudent(this.selectedStudent.name, this.user.uuid)
          this.selectedStudent.uuid = newStudentResp.uuid
        }
        let resp = await postStudentContent(
          this.selectedStudent.uuid,
          CONTENT_TYPES.RESOURCE,
          this.contentUuid,
          this.user.uuid,
        )
        this.saveResult = resp.collection
        this.saving = false
        this.showSaveSuccess = true
        const saveResultUuid = this.saveResult?.uuid || ''
        this.$sendEvent('Resource Saved to Library', {
          student_uuid: saveResultUuid,
          user_folder_role: this.getUserRoleForFolder(saveResultUuid)
        })
      } catch (err) {
        this.showError = true
        this.saveError = (err && err.message) || err
        console.error(err)
      }
    },
    handleTabChange({ isSharedTab }) {
      this.isSharedWithMeTabActive = isSharedTab
      this.selectedStudent = null
    }
  },
  watch: {
    open() {
      this.show = this.open
      this.selectedStudent = (this.recentStudents && this.recentStudents[0]) || null
    }
  },
  eventContext() {
    return {
      resource_uuid: this.contentUuid
    }
  }
}
</script>

<style lang="less" scoped>
.close {
  color: var(--gray-weak);
  opacity: 1;

  &:hover {
    color: var(--gray-default);
    opacity: 1;
  }

  &:focus {
    opacity: 1;
  }
}

.send-icon {
  vertical-align: middle;
  margin-right: 8px;
}
</style>
<style lang="less" scoped>
.btn-toolbar .btn {
  float: none;
}
.btn-link {
  border: 1px solid var(--gray-inverted-weak);
  color: var(--gray-default);
  &:hover {
    text-decoration: none;
    border: 1px solid var(--gray-inverted-weak);
    background: var(--gray-border-weak);
    color: var(--gray-default);
  }
  &:focus-visible {
    outline: 2px solid var(--gray-weak);
    outline-offset: 1px;
  }
}
.btn-success:focus-visible {
  outline: 2px solid var(--green-button-hover);
  outline-offset: 1px;
}
</style>
